<template>
  <div
    :class="['d-toggle-wrapper', wrapperClass]"
    v-bind="addClassStyleAttrs($attrs)"
  >
    <label
      v-if="hasSlotContent($slots.default)"
      :class="labelClass"
      :for="id"
      v-bind="labelChildProps"
      data-qa="toggle-label"
    >
      <!-- @slot Slot for the main content -->
      <slot />
    </label>
    <button
      :id="id"
      :role="toggleRole"
      type="button"
      :aria-checked="internalChecked.toString()"
      :disabled="disabled"
      :aria-disabled="disabled.toString()"
      :class="toggleClasses"
      v-bind="inputListeners"
    >
      <span
        v-if="showIcon"
        class="d-toggle__inner"
      />
    </button>
  </div>
</template>

<script>
import { warn } from 'vue';
import { getUniqueString, hasSlotContent, removeClassStyleAttrs, addClassStyleAttrs } from '@/common/utils';
import { TOGGLE_CHECKED_VALUES, TOGGLE_SIZE_MODIFIERS } from '@/components/toggle/toggle_constants';

/**
 * A toggle (or "switch") is a button control element that allows the user to make a binary (on/off) selection.
 * @see https://dialtone.dialpad.com/components/toggle.html
 */
export default {
  compatConfig: { MODE: 3 },

  name: 'DtToggle',

  inheritAttrs: false,

  props: {

    /**
     * The id of the toggle
     */
    id: {
      type: String,
      default () { return getUniqueString(); },
    },

    /**
     * Disables the toggle interactions
     * @values true, false
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Value of the toggle
     * @model modelValue
     * @values true, false, 'mixed'
     */
    modelValue: {
      type: [Boolean, String],
      default: false,
      validator: (v) => TOGGLE_CHECKED_VALUES.includes(v),
    },

    /**
     * Whether the component toggles on click. If you set this to false it means you will handle the toggling manually
     * via the checked prop or v-model. Change events will still be triggered.
     * @values true, false
     */
    toggleOnClick: {
      type: Boolean,
      default: true,
    },

    /**
     * The size of the toggle.
     * @values sm, md
     */
    size: {
      type: String,
      default: 'md',
      validator: (s) => Object.keys(TOGGLE_SIZE_MODIFIERS).includes(s),
    },

    /**
     * Shows the icon
     * @values true, false
     */
    showIcon: {
      type: Boolean,
      default: true,
    },

    /**
     * Used to customize the label container
     */
    labelClass: {
      type: [String, Array, Object],
      default: '',
    },

    /**
     * Additional styling for the wrapper element
     */
    wrapperClass: {
      type: [String, Array, Object],
      default: undefined,
    },

    /**
     * A set of props that are passed into the label container
     */
    labelChildProps: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: [
    /**
     * Toggle change event
     *
     * @event change
     * @type {Boolean}
     * @model change
     */
    'change',

    /**
     * v-model event event
     *
     * @event change
     * @type {Boolean}
     * @model change
     */
    'update:modelValue',
  ],

  data () {
    return {
      internalChecked: this.modelValue,
      hasSlotContent,
    };
  },

  computed: {
    inputListeners () {
      return {
        ...removeClassStyleAttrs(this.$attrs),
        onClick: () => this.toggleCheckedValue(),
      };
    },

    isIndeterminate () {
      return this.internalChecked === 'mixed';
    },

    toggleRole () {
      return this.isIndeterminate ? 'checkbox' : 'switch';
    },

    toggleClasses () {
      return [
        'd-toggle',
        TOGGLE_SIZE_MODIFIERS[this.size],
        {
          'd-toggle--checked': this.internalChecked === true,
          'd-toggle--disabled': this.disabled,
          'd-toggle--indeterminate': this.isIndeterminate,
        },
      ];
    },
  },

  watch: {
    modelValue (newChecked) {
      this.internalChecked = newChecked;
    },
  },

  mounted () {
    this.runValidations();
  },

  methods: {
    addClassStyleAttrs,
    toggleCheckedValue () {
      this.$emit('change', !this.internalChecked);
      this.$emit('update:modelValue', !this.internalChecked);

      if (this.toggleOnClick) {
        this.internalChecked = !this.internalChecked;
      }
    },

    hasSlotLabel () {
      return !!(this.$slots.default);
    },

    runValidations () {
      this.validateInputLabels(this.hasSlotLabel(), this.$attrs['aria-label']);
    },

    validateInputLabels (hasLabel, ariaLabel) {
      if (!hasLabel && !ariaLabel) {
        warn(
          'You must provide an aria-label when there is no label passed',
          this,
        );
      }
    },
  },
};
</script>
